import React, { useMemo } from "react"
import { Link, useLocation, matchPath } from "react-router-dom"

type Segment = { label: string; to?: string }

const SEGMENTS_BY_PATH: Record<string, Segment[]> = {
  "/properties": [{ label: "Home", to: "/" }, { label: "Properties" }],
  "/live-auction": [{ label: "Home", to: "/" }, { label: "Live Auction" }],
  "/subscription": [{ label: "Home", to: "/" }, { label: "Subscription" }],
  "/broker": [{ label: "Home", to: "/" }, { label: "Broker" }],
  "/buyer": [{ label: "Home", to: "/" }, { label: "Buyer" }],
  "/seller": [{ label: "Home", to: "/" }, { label: "Seller" }],
  "/terms-and-conditions": [{ label: "Home", to: "/" }, { label: "Terms and Conditions" }],
  "/privacy-policy": [{ label: "Home", to: "/" }, { label: "Privacy Policy" }],
  "/about-us": [{ label: "Home", to: "/" }, { label: "About Us" }],
  "/contact-us": [{ label: "Home", to: "/" }, { label: "Contact Us" }],
}

function segmentsForPath(pathname: string): Segment[] {
  const path = pathname.replace(/\/+$/, "") || "/"
  if (path === "/") return []
  if (matchPath({ path: "/property/:id", end: true }, path)) {
    return [
      { label: "Home", to: "/" },
      { label: "Properties", to: "/properties" },
      { label: "Property" },
    ]
  }
  return SEGMENTS_BY_PATH[path] ?? []
}

export type SoldxBreadcrumbVariant = "hero" | "page"

/** Hero layout only: `dark` = text for dark image overlay (default); `light` = text for light hero background */
export type SoldxBreadcrumbHeroTone = "dark" | "light"

interface SoldxBreadcrumbProps {
  variant?: SoldxBreadcrumbVariant
  /** When `variant="hero"`, pick contrast for the hero background. Ignored for `page`. */
  heroTone?: SoldxBreadcrumbHeroTone
  className?: string
}

const SoldxBreadcrumb: React.FC<SoldxBreadcrumbProps> = ({
  variant = "page",
  heroTone = "dark",
  className,
}) => {
  const { pathname } = useLocation()
  const items = useMemo(() => segmentsForPath(pathname), [pathname])
  if (items.length === 0) return null

  const barClass =
    variant === "hero"
      ? "soldx-breadcrumb-bar soldx-breadcrumb-bar--hero"
      : "soldx-breadcrumb-bar soldx-breadcrumb-bar--page"

  const listClass = [
    "soldx-breadcrumb",
    variant === "hero" && "soldx-breadcrumb--hero",
    variant === "hero" && heroTone === "light" && "soldx-breadcrumb--hero-light",
  ]
    .filter(Boolean)
    .join(" ")

  const navClass = [barClass, className].filter(Boolean).join(" ")

  return (
    <nav className={navClass} aria-label="Breadcrumb">
      <div className="soldx-breadcrumb-inner">
        <ol className={listClass}>
          {items.map((item, index) => {
            const isLast = index === items.length - 1
            return (
              <li key={`${item.label}-${index}`} className="soldx-breadcrumb-item">
                {!isLast && item.to ? (
                  <Link className="soldx-breadcrumb-link" to={item.to}>
                    {item.label}
                  </Link>
                ) : (
                  <span className={isLast ? "soldx-breadcrumb-current" : "soldx-breadcrumb-static"}>
                    {item.label}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </div>
    </nav>
  )
}

export default SoldxBreadcrumb
